/**
 * 备份导入服务
 * 提供从备份文件恢复数据的功能
 */
import { v4 as uuidv4 } from 'uuid';

/**
 * 从备份文件恢复数据
 * @param {File} file - 要导入的备份文件
 * @returns {Promise<Object>} - 包含恢复结果的对象
 */
export const importPromptsFromJson = async (file) => {
  try {
    // 读取JSON文件
    const reader = new FileReader();
    const jsonContent = await new Promise((resolve, reject) => {
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
    
    // 解析JSON内容
    let prompts = [];
    try {
      const jsonData = JSON.parse(jsonContent);
      
      // 处理不同的JSON格式
      if (Array.isArray(jsonData)) {
        // 直接是提示词数组
        prompts = jsonData;
      } else if (jsonData.prompts && Array.isArray(jsonData.prompts)) {
        // 包含prompts字段的对象
        prompts = jsonData.prompts;
      } else if (jsonData.title && jsonData.versions) {
        // 单个提示词对象
        prompts = [jsonData];
      } else {
        throw new Error('无法识别的JSON格式');
      }
      
      // 验证和修复提示词
      prompts = prompts.map(validateAndFixPrompt);
      
      return {
        success: true,
        prompts,
        count: prompts.length
      };
    } catch (parseError) {
      console.error('解析JSON失败:', parseError);
      return {
        success: false,
        error: '无法解析JSON文件，请确保文件格式正确'
      };
    }
  } catch (error) {
    console.error('导入JSON文件失败:', error);
    return {
      success: false,
      error: error.message || '导入JSON文件失败'
    };
  }
};

/**
 * 验证并修复提示词对象，确保所有必要的字段都存在
 * @param {Object} prompt - 提示词对象
 * @returns {Object} - 修复后的提示词对象
 */
const validateAndFixPrompt = (prompt) => {
  const now = new Date().toISOString();
  
  // 确保基本字段存在
  const validatedPrompt = {
    id: prompt.id || uuidv4(),
    title: prompt.title || '导入的提示词',
    introduction: prompt.introduction || '',
    folderId: prompt.folderId || null,
    folderName: prompt.folderName || null, // 保留文件夹名称以便导入时匹配
    tags: Array.isArray(prompt.tags) ? prompt.tags : [],
    tagNames: Array.isArray(prompt.tagNames) ? prompt.tagNames : [], // 保留标签名称以便导入时匹配
    createdAt: prompt.createdAt || now,
    updatedAt: prompt.updatedAt || now,
    currentVersionIndex: typeof prompt.currentVersionIndex === 'number' ? prompt.currentVersionIndex : 0,
    versions: []
  };
  
  // 确保版本数组存在且有效
  if (Array.isArray(prompt.versions) && prompt.versions.length > 0) {
    validatedPrompt.versions = prompt.versions.map((version, index) => {
      // 处理结果图片
      let resultImages = [];
      if (Array.isArray(version.resultImages) && version.resultImages.length > 0) {
        resultImages = version.resultImages.map(img => ({
          url: img.url || '',
          key: img.key || '',
          uid: img.uid || `img-${uuidv4()}`,
          name: img.name || `image-${index}`,
          versionNumber: img.versionNumber || version.versionNumber || `${index + 1}.0`
        })).filter(img => img.url); // 只保留有URL的图片
      }
      
      return {
        id: version.id || uuidv4(),
        content: version.content || '',
        description: version.description || `版本 ${index + 1}`,
        createdAt: version.createdAt || now,
        versionNumber: version.versionNumber || `${index + 1}.0`,
        tags: Array.isArray(version.tags) ? version.tags : [],
        resultImages: resultImages // 添加结果图片字段
      };
    });
  } else {
    // 如果没有版本，创建一个默认版本
    validatedPrompt.versions = [
      {
        id: uuidv4(),
        content: '',
        description: '初始版本',
        createdAt: now,
        versionNumber: '1.0',
        tags: [],
        resultImages: [] // 增加空的结果图片数组
      }
    ];
  }
  
  // 确保currentVersionIndex在有效范围内
  if (validatedPrompt.currentVersionIndex >= validatedPrompt.versions.length) {
    validatedPrompt.currentVersionIndex = validatedPrompt.versions.length - 1;
  }
  
  return validatedPrompt;
};

// 创建导入服务对象
const importService = {
  importPromptsFromJson
};

export default importService;
export { validateAndFixPrompt };
